/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { Box, Stack, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import EditorialSection from '../components/editorial/EditorialSection';
import EditorialButton from '../components/editorial/EditorialButton';
import { getPaymentReceipt } from '../api/payments/receiptApi';
import { getCheckoutSuccessCopy } from '../service/paymentThankYou';

// Shared thank-you for a website donation (default), an invoice payment
// (?context=invoice), and a payment-plan first installment (?context=plan).
// Also still handles the OLDER Stripe Checkout Session membership flow
// (?id=membership, no PaymentIntent/redirect params at all) — that branch
// is untouched.
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 6;

export default function CheckoutSuccess() {
  const storedUid = Cookies.get('uid');
  const storedSessionId = localStorage.getItem('sessionId');

  const params = new URLSearchParams(window.location.search);
  const paymentIntentId = params.get('payment_intent');
  const redirectStatus = params.get('redirect_status');
  const copy = getCheckoutSuccessCopy(params.get('context'));

  const [receipt, setReceipt] = useState(null);
  const [pollsRemaining, setPollsRemaining] = useState(MAX_POLL_ATTEMPTS);
  const attemptsRef = useRef(0);

  useEffect(() => {
    const saveMembershipData = async () => {
      try {
        const id = params.get('id');
        if (id === 'membership') {
          const response = await fetch(`${process.env.REACT_APP_BASE_URL}/payment-success-membership`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: storedUid, sessionId: storedSessionId }),
          });
          if (!response.ok) throw new Error('Failed to save data');
          await response.json();
        }
      } catch (e) {
        console.error('Error saving data:', e.message);
      }
    };
    saveMembershipData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same polling pattern as MembershipThankYou.js: the receipt PDF is
  // generated asynchronously by the backend's Stripe webhook, which can
  // still be running right after the client-side redirect — "not ready
  // yet" is never treated as a failure, since redirect_status already
  // confirmed the payment succeeded.
  useEffect(() => {
    if (!paymentIntentId || redirectStatus !== 'succeeded') return undefined;

    let cancelled = false;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const result = await getPaymentReceipt(paymentIntentId);
        if (cancelled) return;
        setReceipt(result);
        if (result.ready || attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setPollsRemaining(0);
          return;
        }
        setPollsRemaining(MAX_POLL_ATTEMPTS - attemptsRef.current);
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        console.error('Could not check receipt status:', err.message);
        if (!cancelled) setPollsRemaining(0);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIntentId, redirectStatus]);

  const stillWaiting = pollsRemaining > 0 && !receipt?.ready;
  // The old Checkout Session membership flow (?id=membership) never sends
  // redirect_status at all — treat "absent" the same as "succeeded" so
  // that flow's thank-you page is unchanged from before this update.
  const paymentFailed = redirectStatus && redirectStatus !== 'succeeded';

  return (
    <EditorialPageShell title="Thank you" description="Your gift to KBT was received.">
      <Box
        component="section"
        sx={{
          position: 'relative',
          background: 'var(--navy-900)',
          color: '#fff',
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          pt: { xs: 18, md: 22 },
          pb: { xs: 10, md: 14 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(7,6,26,0.72) 0%, rgba(7,6,26,0.92) 100%), url('/assets/images/kbt/siyum.jpg') center/cover no-repeat",
            opacity: 0.6,
          }}
        />
        <Box sx={{ position: 'relative', maxWidth: 900, mx: 'auto', px: { xs: 3, md: 6 }, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: 'var(--teal-300)',
                mb: 4,
              }}
            >
              {copy.kicker}
            </Box>

            {paymentFailed ? (
              <>
                <Box
                  sx={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: 'clamp(32px, 5.5vw, 60px)',
                    lineHeight: 1.05,
                    letterSpacing: '-0.018em',
                    color: '#fff',
                    mb: 3,
                  }}
                >
                  {redirectStatus === 'processing'
                    ? copy.processingTitle || 'Your payment is processing.'
                    : copy.failTitle || "We couldn't confirm that payment."}
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'clamp(15px, 1.4vw, 18px)',
                    lineHeight: 1.6,
                    color: 'rgba(255,255,255,0.85)',
                    maxWidth: 620,
                    mx: 'auto',
                    mb: 6,
                  }}
                >
                  {redirectStatus === 'processing' ? (
                    copy.processingBody || "This can take a moment — you'll get an email confirmation once it clears."
                  ) : copy.failBody ? (
                    copy.failBody
                  ) : (
                    <>
                      No charge went through. Please try again, or reach out if you keep seeing this.{' '}
                      <span style={{ color: 'var(--teal-300)' }}>office@kbtla.org</span>.
                    </>
                  )}
                </Box>
                <EditorialButton variant="primary" href={copy.failHref}>
                  {copy.failLabel}
                </EditorialButton>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: 'clamp(40px, 7vw, 90px)',
                    lineHeight: 0.98,
                    letterSpacing: '-0.018em',
                    color: '#fff',
                    mb: 3,
                  }}
                >
                  {copy.titleAccent ? (
                    <>
                      {copy.title}{' '}
                      <span style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--teal-300)' }}>
                        {copy.titleAccent}
                      </span>
                    </>
                  ) : (
                    copy.title
                  )}
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'clamp(15px, 1.4vw, 18px)',
                    lineHeight: 1.6,
                    color: 'rgba(255,255,255,0.85)',
                    maxWidth: 620,
                    mx: 'auto',
                    mb: 5,
                  }}
                >
                  {copy.body}
                  {copy.noReceiptLine ? (
                    <>
                      {' '}If anything looks off, write to{' '}
                      <span style={{ color: 'var(--teal-300)' }}>office@kbtla.org</span>.
                    </>
                  ) : (
                    <>
                      {' '}A receipt is on its way to your email. If anything looks off, write to{' '}
                      <span style={{ color: 'var(--teal-300)' }}>office@kbtla.org</span>.
                    </>
                  )}
                </Box>

                <Stack spacing={2.5} alignItems="center">
                  {paymentIntentId && (
                    receipt?.ready ? (
                      <EditorialButton variant="primary" href={receipt.receiptPdfUrl} target="_blank" rel="noreferrer">
                        Download Receipt
                      </EditorialButton>
                    ) : stillWaiting ? (
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ opacity: 0.85 }}>
                        <CircularProgress size={18} sx={{ color: 'var(--teal-300)' }} />
                        <Box sx={{ fontFamily: 'var(--font-body)', fontSize: 14 }}>
                          Preparing your receipt…
                        </Box>
                      </Stack>
                    ) : (
                      <Box sx={{ fontFamily: 'var(--font-body)', fontSize: 14, opacity: 0.75 }}>
                        Your receipt is on its way to your email.
                      </Box>
                    )
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                    <EditorialButton variant="primary" href={copy.primaryHref}>
                      {copy.primaryLabel}
                    </EditorialButton>
                    <EditorialButton variant="inverse" href="/events">
                      See Upcoming Events
                    </EditorialButton>
                  </Stack>
                </Stack>
              </>
            )}
          </motion.div>
        </Box>
      </Box>

      <EditorialSection
        kicker="What's next"
        title="The kehilla, every day."
        intro="If you've never stepped inside, this is your invitation. Daf at dawn, shiurim through the week, and an open table any time."
        align="center"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: { xs: 4, md: 5 },
            mt: 2,
          }}
        >
          {[
            { kicker: 'Daily', head: 'Schedule of Tefillah', href: '/prayers' },
            { kicker: 'Weekly', head: 'Classes & Shiurim', href: '/classes' },
            { kicker: 'Anytime', head: 'Write to the kehilla', href: '/contact-us' },
          ].map((c) => (
            <Box
              key={c.head}
              component="a"
              href={c.href}
              sx={{
                textDecoration: 'none',
                borderTop: '1px solid rgba(36,34,88,0.18)',
                pt: 4,
                display: 'block',
                transition: 'transform 250ms ease',
                '&:hover': { transform: 'translateY(-2px)' },
              }}
            >
              <Box
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: 'var(--teal-500)',
                  mb: 2,
                }}
              >
                {c.kicker}
              </Box>
              <Box
                sx={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: { xs: 24, md: 30 },
                  color: 'var(--navy-500)',
                  lineHeight: 1.1,
                  letterSpacing: '-0.01em',
                }}
              >
                {c.head}  →
              </Box>
            </Box>
          ))}
        </Box>
      </EditorialSection>
    </EditorialPageShell>
  );
}
